import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * In-memory holder for local audio files picked from the device.
 *
 * There's no persistent web API on iOS that gives us access to the whole
 * music library, so this is deliberately session-scoped: the user picks
 * files from a native picker (or the OS share sheet on installed PWAs)
 * and we hold the File objects here until they close the tab.
 *
 * `reference` is the single track chosen for song-generation reference
 * audio. When set, the generator page reads it, converts to base64, and
 * ships it as `referenceAudio` in the job payload — bypassing the iTunes
 * lookup entirely.
 *
 * NOTE: File objects are not serializable — this only works because the
 * store is configured with `serializableCheck: false`. Never persist this
 * slice to localStorage.
 */
export interface LocalTrack {
  id: string;      // client-side uuid — File itself isn't identifiable across pickers
  file: File;      // the raw file, held in memory
  name: string;
  size: number;
  addedAt: number;
}

interface LocalAudioState {
  tracks: LocalTrack[];       // everything picked this session, most recent first
  referenceId: string | null; // id of the track flagged for generation reference
}

const initialState: LocalAudioState = { tracks: [], referenceId: null };

const localAudioSlice = createSlice({
  name: 'localAudio',
  initialState,
  reducers: {
    addTracks(state, action: PayloadAction<LocalTrack[]>) {
      // Dedup by (name, size) — same file picked twice shouldn't duplicate.
      const existing = new Set(state.tracks.map((t) => `${t.name}|${t.size}`));
      const fresh = action.payload.filter((t) => !existing.has(`${t.name}|${t.size}`));
      state.tracks = [...fresh, ...state.tracks];
    },
    removeTrack(state, action: PayloadAction<string>) {
      state.tracks = state.tracks.filter((t) => t.id !== action.payload);
      if (state.referenceId === action.payload) state.referenceId = null;
    },
    clearTracks(state) {
      state.tracks = [];
      state.referenceId = null;
    },
    setReferenceId(state, action: PayloadAction<string | null>) {
      state.referenceId = action.payload;
    },
  },
});

export const { addTracks, removeTrack, clearTracks, setReferenceId } = localAudioSlice.actions;
export default localAudioSlice.reducer;
